import type { Action } from '@reduxjs/toolkit';
import type { ClientCategory, LicenceCsm, LicenceResponse } from '@shared/schemas';
import { all, call, delay, getContext, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { classifyInfraError } from '@/utils/infra-error';

import { toAddLicenceError } from '../domain/add-licence-error';
import type { LicenceDraft } from '../domain/licence-draft.model';
import { toCreateRequest, validateDraft } from '../domain/licence-draft.validation';
import type { AddLicenceGateway } from '../infrastructure/add-licence.gateway';
import {
  clientCategoriesRequested,
  clientCategoryAddRequested,
  csmCandidatesRequested,
  submitRequested,
} from './add-licence.actions';
import { selectDraft } from './add-licence.selectors';
import { addLicenceActions } from './add-licence.slice';

/**
 * WORKER: create a licence from the current draft. Validates up front (a
 * `validation` error short-circuits before any I/O), then all I/O goes through
 * the injected gateway (read from the saga `context`; never the api client), so
 * tests drive the success / api-error / network-error branches with an in-memory
 * gateway. `takeLatest` means a fresh submit supersedes any in-flight save.
 */
function* saveWorker() {
  const draft = (yield select(selectDraft)) as LicenceDraft;

  const errors = validateDraft(draft);
  const request = toCreateRequest(draft);
  if (Object.keys(errors).length > 0 || !request) {
    yield put(addLicenceActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  yield put(addLicenceActions.saveStarted());
  const gateway: AddLicenceGateway = yield getContext('addLicenceGateway');
  try {
    const licence = (yield call(gateway.createLicence, request)) as LicenceResponse;
    yield put(addLicenceActions.saveSucceeded(licence.id));
  } catch (e) {
    yield put(addLicenceActions.saveFailed(toAddLicenceError(e)));
  }
}

/**
 * WORKER: live name-availability probe. `takeLatest` + a leading `delay` gives
 * debounce WITH cancellation of the in-flight request (the `debounce` effect
 * would let a slow response for "Acm" clobber the verdict for "Acme"). Errors
 * fail open (`nameCheckFailed` → 'idle'): a broken probe never blocks creation,
 * the backend 409 is the real guard.
 */
function* checkNameWorker() {
  yield delay(350); // debounce; cancelled by the next keystroke
  const draft = (yield select(selectDraft)) as LicenceDraft;
  const name = draft.name.trim();
  if (!name) return; // fieldChanged already reset the verdict to 'idle'

  const gateway: AddLicenceGateway = yield getContext('addLicenceGateway');
  yield put(addLicenceActions.nameCheckStarted());
  try {
    const exists = (yield call(gateway.checkNameExists, name)) as boolean;
    yield put(addLicenceActions.nameCheckSucceeded(exists));
  } catch {
    yield put(addLicenceActions.nameCheckFailed());
  }
}

/**
 * WORKER: load who may be picked as the Customer Success Manager (#583). A
 * failure leaves the select on "None": the CSM is optional, so it never blocks
 * creating the licence.
 */
function* csmCandidatesWorker() {
  const gateway: AddLicenceGateway = yield getContext('addLicenceGateway');
  yield put(addLicenceActions.csmCandidatesLoadStarted());
  try {
    const candidates = (yield call(gateway.loadCsmCandidates)) as LicenceCsm[];
    yield put(addLicenceActions.csmCandidatesLoadSucceeded(candidates));
  } catch {
    yield put(addLicenceActions.csmCandidatesLoadFailed());
  }
}

/**
 * WORKER: load the client category list (#604). A failure leaves the select
 * empty with a message: the category is required, so the form cannot be sent
 * until the list comes back; reopening it retries.
 */
function* clientCategoriesWorker() {
  const gateway: AddLicenceGateway = yield getContext('addLicenceGateway');
  yield put(addLicenceActions.clientCategoriesLoadStarted());
  try {
    const categories = (yield call(gateway.loadClientCategories)) as ClientCategory[];
    yield put(addLicenceActions.clientCategoriesLoadSucceeded(categories));
  } catch {
    yield put(addLicenceActions.clientCategoriesLoadFailed());
  }
}

/**
 * WORKER: add a client category, then pick it (#604). The server's refusal (a
 * duplicate, an empty or too long name, not an Administrator) is shown under
 * the input as it comes.
 */
function* clientCategoryAddWorker(action: ReturnType<typeof clientCategoryAddRequested>) {
  const gateway: AddLicenceGateway = yield getContext('addLicenceGateway');
  yield put(addLicenceActions.clientCategoryAddStarted());
  try {
    const category = (yield call(gateway.addClientCategory, action.payload)) as ClientCategory;
    yield put(addLicenceActions.clientCategoryAddSucceeded(category));
  } catch (e) {
    yield put(
      addLicenceActions.clientCategoryAddFailed(
        classifyInfraError(e, "The category couldn't be added.").message,
      ),
    );
  }
}

/** WATCHERS. */
export function* addLicenceSaga() {
  yield all([
    takeLatest(submitRequested.type, saveWorker),
    takeLatest(csmCandidatesRequested.type, csmCandidatesWorker),
    takeLatest(clientCategoriesRequested.type, clientCategoriesWorker),
    // `takeLeading`: a double Enter must not send the same category twice.
    takeLeading(clientCategoryAddRequested.type, clientCategoryAddWorker),
    // Only a change to the NAME re-probes it; picking a category or a country
    // must not cancel an in-flight check nor fire a new one.
    takeLatest(
      (action: Action) =>
        addLicenceActions.fieldChanged.match(action) && action.payload.field === 'name',
      checkNameWorker,
    ),
  ]);
}
